import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DatePicker, Input, Button, Table, Modal, Tag, message, Select, ConfigProvider } from 'antd';
import { SearchOutlined, ReloadOutlined, CopyOutlined, CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { CommissionManagementPageModel } from '../pageModels/CommissionManagementPageModel';
import ShowHint from '../../components/ShowHint';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import zhCN from 'antd/locale/zh_CN';
import '../styles/CommissionManagement.css';

// 设置dayjs为中文
dayjs.locale('zh-cn');

const CommissionManagement: React.FC = observer(() => {
    const [model] = useState(() => new CommissionManagementPageModel());
    const [copySuccess, setCopySuccess] = useState(false);

    useEffect(() => {
        // 页面加载时自动查询数据
        model.queryData();
    }, [model]);

    // 处理月份变化
    const handleMonthChange = (date: any) => {
        if (date) {
            const year = date.year();
            const month = String(date.month() + 1).padStart(2, '0');
            model.setSelectedMonth(`${year}-${month}`);
        }
    };

    // 处理搜索
    const handleSearch = () => {
        model.queryData();
    };

    // 处理重置
    const handleReset = () => {
        model.resetFilters();
        // 重置后自动查询数据
        setTimeout(() => model.queryData(), 100);
    };

    // 复制文本到剪贴板
    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            message.error('复制失败');
        }
    };

    // 表格列定义
    const columns = [
        {
            title: '提现所属月份',
            dataIndex: 'periodMonth',
            key: 'periodMonth',
            width: 120,
            render: (text: string) => text.replace('-', '年') + '月'
        },
        {
            title: '卖家ID',
            dataIndex: 'sellerId',
            key: 'sellerId',
            width: 120,
            render: (text: string) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{text}</span>
                    <CopyOutlined 
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => copyToClipboard(text)}
                    />
                </div>
            )
        },
        {
            title: '卖家姓名',
            dataIndex: 'sellerName',
            key: 'sellerName',
            width: 100
        },
        {
            title: '卖家手机号',
            dataIndex: 'sellerPhone',
            key: 'sellerPhone',
            width: 140,
            render: (text: string) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{text}</span>
                    <CopyOutlined 
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => copyToClipboard(text)}
                    />
                </div>
            )
        },
        {
            title: '可提现总收入',
            dataIndex: 'currentAvailableIncome',
            key: 'currentAvailableIncome',
            width: 120,
            render: (value: number) => {
                if (value === null || value === undefined) return '-';
                return `${value.toLocaleString()}元`;
            }
        },
        {
            title: '本月提现申请',
            dataIndex: 'withdrawAmount',
            key: 'withdrawAmount',
            width: 120,
            render: (value: number) => {
                if (value === null || value === undefined) return '-';
                return `${value.toLocaleString()}元`;
            }
        },
        {
            title: '代缴税',
            dataIndex: 'taxAmount',
            key: 'taxAmount',
            width: 100,
            render: (value: number) => {
                if (value === null || value === undefined) return '-';
                return `${value.toLocaleString()}元`;
            }
        },
        {
            title: '年度总提现',
            dataIndex: 'yearTotalWithdrawAmount',
            key: 'yearTotalWithdrawAmount',
            width: 120,
            render: (value: number) => {
                if (value === null || value === undefined) return '-';
                return `${value.toLocaleString()}元`;
            }
        },
        {
            title: '年度平台纳税',
            dataIndex: 'yearTotalTaxAmount',
            key: 'yearTotalTaxAmount',
            width: 130,
            render: (value: number) => {
                if (value === null || value === undefined) return '-';
                return `${value.toLocaleString()}元`;
            }
        },
        {
            title: '提现提交时间',
            dataIndex: 'withdrawPostTime',
            key: 'withdrawPostTime',
            width: 160,
            render: (date: Date) => {
                if (!date) return '-';
                try {
                    return date.toLocaleString('zh-CN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                } catch (error) {
                    return '-';
                }
            }
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: number) => (
                <Tag color={model.getStatusColor(status)}>
                    {model.getStatusText(status)}
                </Tag>
            )
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, record: any) => (
                record.status === 0 || record.status === 1 ? (
                    <Button
                        type="link"
                        size="small"
                        onClick={() => model.markOneAsPaid(record.withdrawId)}
                        style={{ padding: 0, height: 'auto' }}
                    >
                        标记发放
                    </Button>
                ) : null
            )
        }
    ];

    return (
        <ConfigProvider locale={zhCN}>
            {/* 筛选区域 */}
            <div className="cm-filter-section">
                <div className="cm-filter-row">
                    <div className="cm-filter-left">
                        <div className="cm-filter-item"> 
                            <DatePicker
                                defaultValue={model.selectedMonth ? dayjs(model.selectedMonth + '-01') : undefined}
                                onChange={handleMonthChange}
                                picker="month" 
                                placeholder="选择结算月份"
                                style={{ width: 150 }}
                                format="YYYY-MM"
                            />
                        </div>
                        <div className="cm-filter-item"> 
                            <Input
                                placeholder="输入卖家ID或手机号"
                                value={model.searchKeyword}
                                onChange={(e) => model.setSearchKeyword(e.target.value)}
                                onPressEnter={handleSearch}
                                style={{ width: 200, borderRadius: 0 }}
                                prefix={<SearchOutlined />}
                            />
                        </div>
                        <div className="cm-filter-item"> 
                            <Select
                                value={model.statusFilter}
                                onChange={(value) => model.setStatusFilter(value)}
                                placeholder="选择状态"
                                style={{ width: 130 }}
                                allowClear
                            >
                                <Select.Option value="">全部状态</Select.Option>
                                <Select.Option value="0">尚未发放</Select.Option>
                                <Select.Option value="1">锁定中</Select.Option>
                                <Select.Option value="2">已经发放</Select.Option>
                            </Select>
                        </div>
                    </div>
                    <div className="cm-filter-right">
                        <div className="cm-filter-item">
                            <Button 
                                type="primary" 
                                onClick={handleSearch}
                                loading={model.loading}
                                style={{ paddingInline: 40 }}
                            >
                                查询
                            </Button>
                        </div>
                        <div className="cm-filter-item">
                            <Button onClick={handleReset}   style={{ paddingInline: 40 }}>
                                重置
                            </Button>
                        </div>
                        <div className="cm-filter-item">
                            <Button 
                                icon={<DownloadOutlined />}
                                onClick={() => model.exportCsvFile()}
                                disabled={!model.data}
                            >
                                导出CSV
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 总结和全局操作区域 */}
            <div className="cm-summary-section">
                <div className="cm-summary-left">
                    <div className="cm-summary-text">
                        {model.summaryText}
                    </div>
                    <div className="cm-job-status">
                        <Tag color={model.autoWithdrawJobStatusColor}>
                            {model.autoWithdrawJobStatusText}
                        </Tag>
                        <Button
                            type="text"
                            size="small"
                            icon={<ReloadOutlined spin={model.autoWithdrawJobLoading} />}
                            loading={model.autoWithdrawJobLoading}
                            onClick={() => model.showConfirmDialogForTriggerAutoWithdraw()}
                            title="手动触发自动提现"
                        />
                    </div>
                </div>
                <div className="cm-summary-actions">
                    {model.shouldShowMarkAllButton && (
                        <Button 
                            type="primary" 
                            icon={<CheckCircleOutlined />}
                            onClick={() => model.showConfirmDialogForMarkAll()}
                        >
                            完成佣金发放
                        </Button>
                    )}
                </div>
            </div>

            {/* 列表数据展示区域 */}
            <div className="cm-table-section">
                {!model.loading && model.data && model.data.withdraws.length === 0 ? (
                    <div className="cm-empty-state">
                        <p>暂无数据</p>
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={model.data?.withdraws || []}
                        rowKey="withdrawId"
                        loading={model.loading}
                        pagination={false}
                        scroll={{ x: 1500 }}
                        size="middle"
                        locale={{
                            emptyText: '暂无数据'
                        }}
                    />
                )}
            </div>

            {/* 确认弹窗 */}
            <Modal
                title={model.confirmDialogTitle}
                open={model.showConfirmDialog}
                onOk={model.confirmDialogAction || (() => {})}
                onCancel={() => model.hideConfirmDialog()}
                okText="确定"
                cancelText="取消"
            >
                <p>{model.confirmDialogContent}</p>
            </Modal>

            {/* 提示组件 */}
            <ShowHint
                message={model.hintMessage}
                visible={model.showHint}
                onClose={() => model.hideHint()}
                position="top"
            />

            {/* 复制成功提示 */}
            {copySuccess && (
                <ShowHint
                    message="复制成功"
                    visible={copySuccess}
                    onClose={() => setCopySuccess(false)}
                    position="top"
                    duration={2000}
                />
            )}

            {/* 加载状态提示 */}
            {model.loading && (
                <div className="cm-loading-overlay">
                    <div className="cm-loading-content">
                        <p>正在加载数据...</p>
                    </div>
                </div>
            )}
        </ConfigProvider>
    );
});

export default CommissionManagement; 